import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import { useAlert } from 'react-alert'
import { useHistory, useParams } from 'react-router-dom'
import { setPath, setUrlId } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import {
  destroyTaskProjectTemplateSetting,
  fetchTaskLabelSettingNewDetail,
  fetchTaskProjectTemplateSetting,
} from '../../../../states/containers/task/taskSlice'
import { RequestGetTaskProjectTemplateSetting } from '../../../../states/types/task'

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface TemplateDetail {
  id?: number
  name: string
  code: string | null
  color: string
  description: string | null
  label_group_category_ids: number[]
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
        scope: string
      }[]
      members: {
        id: number
        name: string
      }[]
      category_managers: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
        scope: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
    enable_settable_permission: boolean
    settable: {
      teams: {
        id: number
        name: string
        scope: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const Index: FC = () => {
  const {
    task,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const [data, setData] = useState<TemplateDetail[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  // confirmation modal
  const openConfirmationModal = (type: string, id: number, name: string) => {
    let callback = () => {
      // none
    }
    switch (type) {
      case "delete":
        callback = () => {
          dispatch(
            destroyTaskProjectTemplateSetting(
              { id: id.toString() },
              () => {
                alert.success("ユーザーテンプレートを削除しました！")
              },
            ),
          )
        }
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const createTemplate = () => {
    history.push({
      pathname: `${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING_CREATE.path, language)}`,
    })
  }

  const updateTemplate = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_PROJECT_TEMPLATE_SETTING_DETAIL.path, 'id', id), "ja")}`,
      state: { keep: true },
    })
  }

  const buildTargetMember = (team: { id: number, scope: string, name: string }, scope: string) => {
    const targetTeam = task.taskLabelSettingNew?.teams.find((v) => v.id === team.id)
    if (!targetTeam) return []
    if (scope === "manager") {
      return targetTeam.managers
    }
    if (scope === "member") {
      return targetTeam.members
    }
    return Array.from(new Set([...targetTeam.managers, ...targetTeam.members]))
  }

  useEffect(() => {
    const params: RequestGetTaskProjectTemplateSetting = {
      limit: 1000,
    }
    dispatch(
      fetchTaskProjectTemplateSetting(
        params,
        () => {
          setIsLoading(false)
        },
      ),
    )
    dispatch(fetchTaskLabelSettingNewDetail())
  }, [])

  useEffect(() => {
    if (!task.taskProjectTemplateSetting?.label_group_templates) return
    setData(task.taskProjectTemplateSetting.label_group_templates)
  }, [task.taskProjectTemplateSetting])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - ユーザーテンプレート設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        ユーザーテンプレート設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ユーザーテンプレート設定
        </h1>
        <div className="tools">
          <div className="button_area">
            <button
              type="button"
              className="button"
              onClick={createTemplate}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} />
              &nbsp;
              テンプレートを新規作成する
            </button>
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          ユーザーテンプレートを作成・変更できます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              isLoading ? (
                <LoadingSquare>
                  <>
                    ユーザーテンプレートリストを読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    data.length ? (
                      <>
                        <div className="template_table">
                          <div className="row_head">
                            <div>
                              編集
                            </div>
                            <div>
                              セット名
                            </div>
                            <div>
                              セット内容
                            </div>
                          </div>
                          {
                            data.map((detail) => (
                              <div
                                className="row"
                                key={`detail_${detail.name}`}
                              >
                                <div>
                                  <Tips
                                    description="編集する"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'tools']}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => updateTemplate(detail?.id || 0)}
                                      />
                                    </>
                                  </Tips>
                                  <Tips
                                    description="削除する"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'minus-circle']}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openConfirmationModal("delete", detail?.id || 0, `ユーザーテンプレート:${detail.name}`)}
                                      />
                                    </>
                                  </Tips>
                                </div>
                                <div>
                                  <div
                                    className="color"
                                    style={{
                                      backgroundColor: detail.color,
                                    }}
                                  />
                                  {detail.name}
                                  {
                                    detail.description ? (
                                      <Tips
                                        description={detail.description.replace(/\n/g, "%br%")}
                                        className=""
                                        icon={false}
                                      >
                                        <>
                                          <FontAwesomeIcon
                                            icon={['far', 'comment-dots']}
                                          />
                                        </>
                                      </Tips>
                                    ) : (
                                      <></>
                                    )
                                  }
                                </div>
                                <div>
                                  {
                                    // eslint-disable-next-line max-len
                                    (!!detail.permission.viewable.teams.length || !!detail.permission.viewable.members.length || !!detail.permission.viewable.category_managers?.length) ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            {`プロジェクトレポート表示${detail.permission.enable_settable_permission ? "" : "・編集権限"}対象`}
                                          </div>
                                          {
                                            !!detail.label_group_category_ids.length && (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                {
                                                  task.taskLabelSettingNew?.label_group_categories
                                                  .filter((v) => detail.label_group_category_ids.includes(v.id)).map((v) => (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        border: "solid 1px #999999",
                                                        borderRadius: "20px",
                                                        padding: "2px 8px",
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={['fas', 'folder']} style={{ color: v.color }} />
                                                      {v.category_name}
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            )
                                          }
                                          {
                                            !!detail.permission.viewable.category_managers?.length && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "8px",
                                                    borderBottom: "solid 1px #CCCCCC",
                                                    backgroundColor: "#EEEEEE",
                                                  }}
                                                >
                                                  <div>
                                                    <Tips
                                                      description="プロジェクトが所属しているカテゴリーの表示ユーザーです。"
                                                      className=""
                                                    >
                                                      <>
                                                        カテゴリー表示ユーザー
                                                      </>
                                                    </Tips>
                                                  </div>
                                                </div>
                                                <div
                                                  className="template_tips"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    detail.permission.viewable.category_managers.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`category_manager_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            !!detail.permission.viewable.teams?.length && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                  display: "flex",
                                                  flexFlow: "column",
                                                  gap: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  チーム
                                                </div>
                                                {
                                                  detail.permission.viewable.teams.map((team) => (
                                                    <div
                                                      style={{
                                                        border: "solid 1px #CCCCCC",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          justifyContent: "space-between",
                                                          alignItems: "center",
                                                          padding: "8px",
                                                          borderBottom: "solid 1px #CCCCCC",
                                                          backgroundColor: "#EEEEEE",
                                                        }}
                                                      >
                                                        <div>
                                                          {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}`}
                                                        </div>
                                                      </div>
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          flexFlow: "column",
                                                          gap: "12px",
                                                          padding: "12px 8px 8px",
                                                        }}
                                                      >
                                                        {
                                                          ["both", "manager"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                マネージャー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "manager").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                        {
                                                          ["both", "member"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                メンバー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "member").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                      </div>
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            )
                                          }
                                          {
                                            !!detail.permission.viewable.members?.length && (
                                              <>
                                                <div
                                                  style={{
                                                    border: "solid 1px #CCCCCC",
                                                    borderRadius: "4px",
                                                    padding: "16px",
                                                    position: "relative",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 4px",
                                                      position: "absolute",
                                                      top: 0,
                                                      left: "8px",
                                                      transform: "translateY(-50%)",
                                                      fontSize: "10px",
                                                      color: "#666666",
                                                    }}
                                                  >
                                                    ユーザー
                                                  </div>
                                                  <div className="template_tips">
                                                    {
                                                      detail.permission.viewable.members.map((member) => (
                                                        <div
                                                          className="template_tip"
                                                          key={`member_${member.id}`}
                                                        >
                                                          {member.name}
                                                        </div>
                                                      ))
                                                    }
                                                  </div>
                                                </div>
                                              </>
                                            )
                                          }
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    (detail.permission.settable.teams.length || detail.permission.settable.members.length) ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            プロジェクト編集権限対象
                                          </div>
                                          {
                                            !!detail.permission.settable.teams?.length && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                  display: "flex",
                                                  flexFlow: "column",
                                                  gap: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  チーム
                                                </div>
                                                {
                                                  detail.permission.settable.teams.map((team) => (
                                                    <div
                                                      style={{
                                                        border: "solid 1px #CCCCCC",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          justifyContent: "space-between",
                                                          alignItems: "center",
                                                          padding: "8px",
                                                          borderBottom: "solid 1px #CCCCCC",
                                                          backgroundColor: "#EEEEEE",
                                                        }}
                                                      >
                                                        <div>
                                                          {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}`}
                                                        </div>
                                                      </div>
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          flexFlow: "column",
                                                          gap: "12px",
                                                          padding: "12px 8px 8px",
                                                        }}
                                                      >
                                                        {
                                                          ["both", "manager"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                マネージャー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "manager").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                        {
                                                          ["both", "member"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                メンバー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "member").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                      </div>
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            )
                                          }
                                          {
                                            !!detail.permission.settable.members?.length && (
                                              <>
                                                <div
                                                  style={{
                                                    border: "solid 1px #CCCCCC",
                                                    borderRadius: "4px",
                                                    padding: "16px",
                                                    position: "relative",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 4px",
                                                      position: "absolute",
                                                      top: 0,
                                                      left: "8px",
                                                      transform: "translateY(-50%)",
                                                      fontSize: "10px",
                                                      color: "#666666",
                                                    }}
                                                  >
                                                    ユーザー
                                                  </div>
                                                  <div className="template_tips">
                                                    {
                                                      detail.permission.settable.members.map((member) => (
                                                        <div
                                                          className="template_tip"
                                                          key={`member_${member.id}`}
                                                        >
                                                          {member.name}
                                                        </div>
                                                      ))
                                                    }
                                                  </div>
                                                </div>
                                              </>
                                            )
                                          }
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    (detail.permission.usable.teams.length || detail.permission.usable.members.length) ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            プロジェクト業務仕分け対象
                                          </div>
                                          {
                                            !!detail.permission.usable.teams?.length && (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                  display: "flex",
                                                  flexFlow: "column",
                                                  gap: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  チーム
                                                </div>
                                                {
                                                  detail.permission.usable.teams.map((team) => (
                                                    <div
                                                      style={{
                                                        border: "solid 1px #CCCCCC",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          justifyContent: "space-between",
                                                          alignItems: "center",
                                                          padding: "8px",
                                                          borderBottom: "solid 1px #CCCCCC",
                                                          backgroundColor: "#EEEEEE",
                                                        }}
                                                      >
                                                        <div>
                                                          {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}`}
                                                        </div>
                                                      </div>
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          flexFlow: "column",
                                                          gap: "12px",
                                                          padding: "12px 8px 8px",
                                                        }}
                                                      >
                                                        {
                                                          ["both", "manager"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                マネージャー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "manager").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                        {
                                                          ["both", "member"].includes(team.scope) && (
                                                            <div
                                                              style={{
                                                                border: "solid 1px #CCCCCC",
                                                                borderRadius: "4px",
                                                                padding: "8px",
                                                                position: "relative",
                                                                backgroundColor: "#FFFFFF",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  backgroundColor: "#FFFFFF",
                                                                  padding: "0 4px",
                                                                  position: "absolute",
                                                                  top: 0,
                                                                  left: "8px",
                                                                  transform: "translateY(-50%)",
                                                                  fontSize: "10px",
                                                                  color: "#666666",
                                                                }}
                                                              >
                                                                メンバー
                                                              </div>
                                                              <div
                                                                className="template_tips"
                                                                style={{
                                                                  padding: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  buildTargetMember(team, "member").map((member) => (
                                                                    <div
                                                                      className="template_tip"
                                                                      key={`member_${member.id}`}
                                                                    >
                                                                      {member.name}
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            </div>
                                                          )
                                                        }
                                                      </div>
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            )
                                          }
                                          {
                                            !!detail.permission.usable.members?.length && (
                                              <>
                                                <div
                                                  style={{
                                                    border: "solid 1px #CCCCCC",
                                                    borderRadius: "4px",
                                                    padding: "16px",
                                                    position: "relative",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 4px",
                                                      position: "absolute",
                                                      top: 0,
                                                      left: "8px",
                                                      transform: "translateY(-50%)",
                                                      fontSize: "10px",
                                                      color: "#666666",
                                                    }}
                                                  >
                                                    ユーザー
                                                  </div>
                                                  <div className="template_tips">
                                                    {
                                                      detail.permission.usable.members.map((member) => (
                                                        <div
                                                          className="template_tip"
                                                          key={`member_${member.id}`}
                                                        >
                                                          {member.name}
                                                        </div>
                                                      ))
                                                    }
                                                  </div>
                                                </div>
                                              </>
                                            )
                                          }
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    detail.labels.length ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            業務
                                          </div>
                                          <div className="template_tips">
                                            {
                                              detail.labels.map((label) => (
                                                <div
                                                  key={`${detail.name}_${label.name}`}
                                                  className="template_tip"
                                                >
                                                  {label.name}
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="no_data_area">
                          <div className="no_data">
                            テンプレートはありません
                          </div>
                          <div className="no_data_button_area">
                            <button
                              type="button"
                              className="button"
                              onClick={createTemplate}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              &nbsp;
                              テンプレートを新規作成する
                            </button>
                          </div>
                        </div>
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
